// Dashboard filter / date-range state — Dashboard-Filters-01.
//
// Originally FE-031 shipped a minimal context (date range +
// assignees + accounts + category). Dashboard-Filters-01 extends
// the surface to the full v2 set:
//   - date range (preset + custom)
//   - assignees, accounts
//   - categories
//   - tags + regulatory-only toggle
//   - status chips (multi)
//   - account segment (NAICS sector + account tag + primary office)
//
// URL is the source of truth (matches the Commitment Tracker
// pattern at ``frontend/src/features/commitments/urlState.ts``).
// Every header filter survives refresh / deep-link. Custom date
// pickers serialise ``start_date`` / ``end_date`` directly so
// drill-throughs read the same shape.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  UNSAFE_NavigationContext,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { fetchAccountList } from "@/features/accounts/api";
import { fetchTagPicker } from "@/features/commitments/api";

import type { DashboardCommonFilters, DashboardDateRange } from "./types";

export type DateRangePreset = "7d" | "30d" | "90d" | "ytd" | "custom";

const DEFAULT_PRESET: DateRangePreset = "30d";

const VALID_PRESETS: ReadonlySet<DateRangePreset> = new Set([
  "7d", "30d", "90d", "ytd", "custom",
]);

const VALID_STATUSES: ReadonlySet<string> = new Set([
  "backlog", "ready_for_work", "in_progress",
  "in_review", "done", "cancelled",
]);

/** Pure helper: convert a preset into the date-range pair (UTC). */
export function dateRangeForPreset(
  preset: DateRangePreset,
  now: Date = new Date(),
): DashboardDateRange {
  const end = isoDate(now);
  if (preset === "ytd") {
    const startOfYear = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    return { startDate: isoDate(startOfYear), endDate: end };
  }
  const days = preset === "7d" ? 7 : preset === "90d" ? 90 : 30;
  const start = new Date(now.getTime() - days * 86_400_000);
  return { startDate: isoDate(start), endDate: end };
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Composite Account-segment filter — three sub-dimensions, each
 * optional. NAICS sector and Account tag are resolved client-side via
 * the Accounts endpoint into a concrete ``clientFilter`` list (see
 * ``useResolvedClientFilter``). When either is selected with an office,
 * the lookup is narrowed to that office before applying its 200-row cap.
 * The office is also forwarded directly to dashboard APIs as
 * ``primary_location_id`` (ACQ-007). */
export interface AccountSegment {
  naicsSector?: string;
  accountTagId?: string;
  primaryLocationId?: string;
}

export interface DashboardFilterState extends DashboardCommonFilters {
  preset: DateRangePreset;
  /** Resolves the preset (or custom values) into a concrete pair. */
  dateRange: DashboardDateRange;
  /** Regulatory-only toggle — union with manual ``tag`` selections. */
  regulatoryOnly: boolean;
  /** Composite Account segmentation. */
  segment: AccountSegment;
}

interface DashboardFilterContextValue extends DashboardFilterState {
  setPreset: (preset: DateRangePreset) => void;
  setDateRange: (range: DashboardDateRange) => void;
  setAssigneeFilter: (ids: string[]) => void;
  setClientFilter: (ids: string[]) => void;
  setCategory: (slugs: string[]) => void;
  setTag: (ids: string[]) => void;
  setStatuses: (statuses: string[]) => void;
  setRegulatoryOnly: (next: boolean) => void;
  setSegment: (next: AccountSegment) => void;
  reset: () => void;
  /** Tag IDs (CommitmentTagLink.tag_id) flagged ``is_regulatory=true``
   * for this broker. Populated by the provider via the tag picker
   * query so every widget can fold them into its request without a
   * second round-trip. */
  regulatoryTagIds: string[];
}

const DashboardFilterContext =
  createContext<DashboardFilterContextValue | null>(null);

// ── URL serialization helpers ──────────────────────────────────────

const URL_KEYS = {
  preset: "preset",
  start: "start_date",
  end: "end_date",
  assignee: "assignee",
  account: "account",
  category: "category",
  tag: "tag",
  status: "status",
  regulatory: "regulatory",
  naics: "naics",
  accountTag: "account_tag",
  location: "location",
} as const;

function readPreset(sp: URLSearchParams): DateRangePreset {
  const raw = sp.get(URL_KEYS.preset);
  if (raw && VALID_PRESETS.has(raw as DateRangePreset)) {
    return raw as DateRangePreset;
  }
  // If start/end are present without a preset, treat as custom.
  if (sp.get(URL_KEYS.start) && sp.get(URL_KEYS.end)) return "custom";
  return DEFAULT_PRESET;
}

function readStatuses(sp: URLSearchParams): string[] {
  const out = sp.getAll(URL_KEYS.status).filter((s) => VALID_STATUSES.has(s));
  return out;
}

function readBool(value: string | null): boolean {
  return value === "true" || value === "1";
}

function readList(sp: URLSearchParams, key: string): string[] {
  return sp.getAll(key).filter((v) => v !== "");
}

interface ParsedDashboardFilters {
  preset: DateRangePreset;
  customRange: DashboardDateRange | null;
  assigneeFilter: string[];
  clientFilter: string[];
  category: string[];
  tag: string[];
  statuses: string[];
  regulatoryOnly: boolean;
  segment: AccountSegment;
}

export function parseDashboardFiltersFromSearch(
  sp: URLSearchParams,
): ParsedDashboardFilters {
  const preset = readPreset(sp);
  const start = sp.get(URL_KEYS.start);
  const end = sp.get(URL_KEYS.end);
  const customRange = (preset === "custom" && start && end)
    ? { startDate: start, endDate: end }
    : null;
  const segment: AccountSegment = {};
  const naics = sp.get(URL_KEYS.naics);
  if (naics) segment.naicsSector = naics;
  const tag = sp.get(URL_KEYS.accountTag);
  if (tag) segment.accountTagId = tag;
  const loc = sp.get(URL_KEYS.location);
  if (loc) segment.primaryLocationId = loc;
  return {
    preset,
    customRange,
    assigneeFilter: readList(sp, URL_KEYS.assignee),
    clientFilter: readList(sp, URL_KEYS.account),
    category: readList(sp, URL_KEYS.category),
    tag: readList(sp, URL_KEYS.tag),
    statuses: readStatuses(sp),
    regulatoryOnly: readBool(sp.get(URL_KEYS.regulatory)),
    segment,
  };
}

export function serializeDashboardFilters(
  state: ParsedDashboardFilters,
): URLSearchParams {
  const sp = new URLSearchParams();
  if (state.preset !== DEFAULT_PRESET) {
    sp.append(URL_KEYS.preset, state.preset);
  }
  if (state.preset === "custom" && state.customRange) {
    sp.append(URL_KEYS.start, state.customRange.startDate);
    sp.append(URL_KEYS.end, state.customRange.endDate);
  }
  for (const id of state.assigneeFilter) sp.append(URL_KEYS.assignee, id);
  for (const id of state.clientFilter) sp.append(URL_KEYS.account, id);
  for (const slug of state.category) sp.append(URL_KEYS.category, slug);
  for (const id of state.tag) sp.append(URL_KEYS.tag, id);
  for (const s of state.statuses) sp.append(URL_KEYS.status, s);
  if (state.regulatoryOnly) sp.append(URL_KEYS.regulatory, "true");
  if (state.segment.naicsSector) {
    sp.append(URL_KEYS.naics, state.segment.naicsSector);
  }
  if (state.segment.accountTagId) {
    sp.append(URL_KEYS.accountTag, state.segment.accountTagId);
  }
  if (state.segment.primaryLocationId) {
    sp.append(URL_KEYS.location, state.segment.primaryLocationId);
  }
  return sp;
}

// ── Provider ───────────────────────────────────────────────────────

export function DashboardFilterProvider({
  children,
  initialPreset = DEFAULT_PRESET,
}: {
  children: ReactNode;
  initialPreset?: DateRangePreset;
}) {
  // Router presence detector — react-router-dom 6 stores its
  // navigation context on a private symbol. When the dashboard
  // renders without a router (most unit tests for child sections),
  // we fall back to local state so the filter bar still works.
  const hasRouter = useContext(UNSAFE_NavigationContext) !== null;
  if (hasRouter) {
    return (
      <RoutedDashboardFilterProvider initialPreset={initialPreset}>
        {children}
      </RoutedDashboardFilterProvider>
    );
  }
  return (
    <LocalDashboardFilterProvider initialPreset={initialPreset}>
      {children}
    </LocalDashboardFilterProvider>
  );
}


const TAGS_PICKER_QUERY_KEY = "commitments-tags-picker" as const;


function useRegulatoryTagIds(): string[] {
  const q = useQuery({
    queryKey: [TAGS_PICKER_QUERY_KEY],
    queryFn: ({ signal }) => fetchTagPicker({ signal }),
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(
    () => (q.data?.tags ?? []).filter((t) => t.is_regulatory).map((t) => t.id),
    [q.data],
  );
}


function RoutedDashboardFilterProvider({
  children, initialPreset,
}: { children: ReactNode; initialPreset: DateRangePreset }) {
  const location = useLocation();
  const navigate = useNavigate();
  const regulatoryTagIds = useRegulatoryTagIds();

  const sp = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const parsed = useMemo(
    () => parseDashboardFiltersFromSearch(sp),
    [sp],
  );

  // If the URL has no preset hint at all, default to ``initialPreset``
  // — used by tests that mount the provider with a fixed preset.
  const effectivePreset =
    sp.get(URL_KEYS.preset) || sp.get(URL_KEYS.start)
      ? parsed.preset
      : initialPreset;

  const dateRange = useMemo<DashboardDateRange>(() => {
    if (effectivePreset === "custom" && parsed.customRange) {
      return parsed.customRange;
    }
    return dateRangeForPreset(effectivePreset);
  }, [effectivePreset, parsed.customRange]);

  const writeUrl = useCallback(
    (next: ParsedDashboardFilters) => {
      const params = serializeDashboardFilters(next);
      // Preserve unrelated query params already on the URL so the
      // dashboard can coexist with other concerns (e.g. modal
      // ``?modal=`` markers from neighbouring features).
      const preserved = new URLSearchParams(location.search);
      for (const k of Object.values(URL_KEYS)) preserved.delete(k);
      // Sort merged for stable URLs.
      const merged = new URLSearchParams();
      for (const [k, v] of preserved.entries()) merged.append(k, v);
      for (const [k, v] of params.entries()) merged.append(k, v);
      const sorted = new URLSearchParams();
      const entries = Array.from(merged.entries()).sort(
        ([a], [b]) => (a < b ? -1 : a > b ? 1 : 0),
      );
      for (const [k, v] of entries) sorted.append(k, v);
      const qs = sorted.toString();
      navigate(
        { pathname: location.pathname, search: qs ? `?${qs}` : "" },
        { replace: false },
      );
    },
    [location.pathname, location.search, navigate],
  );

  const setPreset = useCallback((nextPreset: DateRangePreset) => {
    writeUrl({
      ...parsed,
      preset: nextPreset,
      customRange: nextPreset === "custom" ? parsed.customRange : null,
    });
  }, [parsed, writeUrl]);

  const setDateRange = useCallback((range: DashboardDateRange) => {
    writeUrl({ ...parsed, preset: "custom", customRange: range });
  }, [parsed, writeUrl]);

  const setAssigneeFilter = useCallback((ids: string[]) => {
    writeUrl({ ...parsed, assigneeFilter: ids });
  }, [parsed, writeUrl]);

  const setClientFilter = useCallback((ids: string[]) => {
    writeUrl({ ...parsed, clientFilter: ids });
  }, [parsed, writeUrl]);

  const setCategory = useCallback((slugs: string[]) => {
    writeUrl({ ...parsed, category: slugs });
  }, [parsed, writeUrl]);

  const setTag = useCallback((ids: string[]) => {
    writeUrl({ ...parsed, tag: ids });
  }, [parsed, writeUrl]);

  const setStatuses = useCallback((statuses: string[]) => {
    const filtered = statuses.filter((s) => VALID_STATUSES.has(s));
    writeUrl({ ...parsed, statuses: filtered });
  }, [parsed, writeUrl]);

  const setRegulatoryOnly = useCallback((next: boolean) => {
    writeUrl({ ...parsed, regulatoryOnly: next });
  }, [parsed, writeUrl]);

  const setSegment = useCallback((next: AccountSegment) => {
    writeUrl({ ...parsed, segment: next });
  }, [parsed, writeUrl]);

  const reset = useCallback(() => {
    writeUrl({
      preset: DEFAULT_PRESET,
      customRange: null,
      assigneeFilter: [],
      clientFilter: [],
      category: [],
      tag: [],
      statuses: [],
      regulatoryOnly: false,
      segment: {},
    });
  }, [writeUrl]);

  const value = useMemo<DashboardFilterContextValue>(
    () => ({
      preset: effectivePreset,
      dateRange,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      assigneeFilter: parsed.assigneeFilter,
      clientFilter: parsed.clientFilter,
      category: parsed.category,
      tag: parsed.tag,
      statuses: parsed.statuses,
      regulatoryOnly: parsed.regulatoryOnly,
      segment: parsed.segment,
      regulatoryTagIds,
      setPreset,
      setDateRange,
      setAssigneeFilter,
      setClientFilter,
      setCategory,
      setTag,
      setStatuses,
      setRegulatoryOnly,
      setSegment,
      reset,
    }),
    [
      effectivePreset, dateRange,
      parsed.assigneeFilter, parsed.clientFilter, parsed.category,
      parsed.tag, parsed.statuses, parsed.regulatoryOnly, parsed.segment,
      regulatoryTagIds,
      setPreset, setDateRange, setAssigneeFilter, setClientFilter,
      setCategory, setTag, setStatuses, setRegulatoryOnly, setSegment,
      reset,
    ],
  );

  return (
    <DashboardFilterContext.Provider value={value}>
      {children}
    </DashboardFilterContext.Provider>
  );
}


/** Pure in-memory provider used when the dashboard mounts outside a
 * router context (unit tests for individual sections). Holds filter
 * state in ``useState`` rather than the URL but exposes the same
 * context shape so widgets render unchanged. */
function LocalDashboardFilterProvider({
  children, initialPreset,
}: { children: ReactNode; initialPreset: DateRangePreset }) {
  const regulatoryTagIds = useRegulatoryTagIds();
  const [state, setState] = useState<ParsedDashboardFilters>({
    preset: initialPreset,
    customRange: null,
    assigneeFilter: [],
    clientFilter: [],
    category: [],
    tag: [],
    statuses: [],
    regulatoryOnly: false,
    segment: {},
  });

  const dateRange = useMemo<DashboardDateRange>(() => {
    if (state.preset === "custom" && state.customRange) {
      return state.customRange;
    }
    return dateRangeForPreset(state.preset);
  }, [state.preset, state.customRange]);

  const setPreset = useCallback((nextPreset: DateRangePreset) => {
    setState((s) => ({
      ...s,
      preset: nextPreset,
      customRange: nextPreset === "custom" ? s.customRange : null,
    }));
  }, []);
  const setDateRange = useCallback((range: DashboardDateRange) => {
    setState((s) => ({ ...s, preset: "custom", customRange: range }));
  }, []);
  const setAssigneeFilter = useCallback((ids: string[]) => {
    setState((s) => ({ ...s, assigneeFilter: ids }));
  }, []);
  const setClientFilter = useCallback((ids: string[]) => {
    setState((s) => ({ ...s, clientFilter: ids }));
  }, []);
  const setCategory = useCallback((slugs: string[]) => {
    setState((s) => ({ ...s, category: slugs }));
  }, []);
  const setTag = useCallback((ids: string[]) => {
    setState((s) => ({ ...s, tag: ids }));
  }, []);
  const setStatuses = useCallback((statuses: string[]) => {
    const filtered = statuses.filter((x) => VALID_STATUSES.has(x));
    setState((s) => ({ ...s, statuses: filtered }));
  }, []);
  const setRegulatoryOnly = useCallback((next: boolean) => {
    setState((s) => ({ ...s, regulatoryOnly: next }));
  }, []);
  const setSegment = useCallback((next: AccountSegment) => {
    setState((s) => ({ ...s, segment: next }));
  }, []);
  const reset = useCallback(() => {
    setState({
      preset: DEFAULT_PRESET,
      customRange: null,
      assigneeFilter: [],
      clientFilter: [],
      category: [],
      tag: [],
      statuses: [],
      regulatoryOnly: false,
      segment: {},
    });
  }, []);

  const value = useMemo<DashboardFilterContextValue>(
    () => ({
      preset: state.preset,
      dateRange,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      assigneeFilter: state.assigneeFilter,
      clientFilter: state.clientFilter,
      category: state.category,
      tag: state.tag,
      statuses: state.statuses,
      regulatoryOnly: state.regulatoryOnly,
      segment: state.segment,
      regulatoryTagIds,
      setPreset, setDateRange, setAssigneeFilter, setClientFilter,
      setCategory, setTag, setStatuses, setRegulatoryOnly, setSegment,
      reset,
    }),
    [
      state, dateRange, regulatoryTagIds,
      setPreset, setDateRange, setAssigneeFilter, setClientFilter,
      setCategory, setTag, setStatuses, setRegulatoryOnly, setSegment,
      reset,
    ],
  );

  return (
    <DashboardFilterContext.Provider value={value}>
      {children}
    </DashboardFilterContext.Provider>
  );
}


export function useDashboardFilters(): DashboardFilterContextValue {
  const ctx = useContext(DashboardFilterContext);
  if (ctx === null) {
    throw new Error(
      "useDashboardFilters must be used within DashboardFilterProvider",
    );
  }
  return ctx;
}

export function useDashboardDateRange(): DashboardDateRange {
  return useDashboardFilters().dateRange;
}

// ── Segment resolution ─────────────────────────────────────────────
//
// The NAICS sector and Account-tag segment dimensions are resolved to a
// concrete ``clientFilter`` list by hitting the /api/v1/accounts/ list
// endpoint. If one of those dimensions is selected with an office, include
// the office in that lookup so its 200-row cap is applied inside the office.
// Office-only selection skips this lookup and goes directly to dashboard
// APIs as ``primary_location_id`` (see ``useCommonFilters``), avoiding the
// old 200-Account truncation. Cached per (office, sector, tag) tuple.

const SEGMENT_QUERY_KEY = "dashboard-segment-accounts" as const;

interface ResolvedSegment {
  clientFilter: string[] | undefined;
  isResolving: boolean;
  segmentMatchedZero: boolean;
}

function useSegmentResolution(): ResolvedSegment {
  const f = useDashboardFilters();
  const segment = f.segment;
  const hasSegment = Boolean(
    segment.naicsSector || segment.accountTagId,
  );

  const segmentQuery = useQuery({
    queryKey: [
      SEGMENT_QUERY_KEY,
      segment.primaryLocationId ?? null,
      segment.naicsSector ?? null,
      segment.accountTagId ?? null,
    ],
    enabled: hasSegment,
    queryFn: async ({ signal }) => {
      const resp = await fetchAccountList(
        {
          filters: {
            naics_sector: segment.naicsSector,
            account_tag_id: segment.accountTagId,
            primary_location_id: segment.primaryLocationId,
          },
          sortBy: "legal_name",
          sortDir: "asc",
          limit: 200,
          offset: 0,
        },
        { signal },
      );
      return resp.rows.map((r) => r.account_id);
    },
    staleTime: 60 * 1000,
  });

  const manual = f.clientFilter ?? [];
  if (!hasSegment) {
    return {
      clientFilter: manual.length > 0 ? manual : undefined,
      isResolving: false,
      segmentMatchedZero: false,
    };
  }
  if (segmentQuery.isLoading || !segmentQuery.data) {
    return {
      clientFilter: manual.length > 0 ? manual : undefined,
      isResolving: true,
      segmentMatchedZero: false,
    };
  }
  const segIds = segmentQuery.data;
  if (segIds.length === 0) {
    return {
      clientFilter: manual,
      isResolving: false,
      segmentMatchedZero: true,
    };
  }
  if (manual.length === 0) {
    return {
      clientFilter: segIds,
      isResolving: false,
      segmentMatchedZero: false,
    };
  }
  const set = new Set(segIds);
  const intersection = manual.filter((id) => set.has(id));
  return {
    clientFilter: intersection,
    isResolving: false,
    segmentMatchedZero: intersection.length === 0,
  };
}


/** Resolved Account-segment lookup. Exposed for the few widgets that
 * need to know whether the segment matched zero accounts (so they
 * can render an explicit empty state). */
export function useResolvedClientFilter(): ResolvedSegment {
  return useSegmentResolution();
}


/** Convenience: pick the slice every endpoint hook actually consumes.
 *
 * Folds in three transformations beyond the raw filter state:
 *   1. Regulatory-only toggle — when ON, every Commitment tag with
 *      ``is_regulatory=true`` is unioned into the wire ``tag[]`` param.
 *      The provider supplies the regulatory tag IDs via context.
 *   2. NAICS sector / Account-tag segment — resolved client-side to a
 *      concrete ``clientFilter`` list. Pass ``opts.skipSegmentResolution =
 *      true`` to opt out (rare).
 *   3. Primary office — forwarded directly as ``officeFilter`` (ACQ-007);
 *      the server applies it, so it is never resolved through the Accounts
 *      endpoint.
 */
export function useCommonFilters(opts?: {
  regulatoryTagIds?: string[];
  skipSegmentResolution?: boolean;
}): DashboardCommonFilters {
  const f = useDashboardFilters();
  const regIds = opts?.regulatoryTagIds ?? f.regulatoryTagIds ?? [];
  const resolved = useSegmentResolution();
  const useResolved = !opts?.skipSegmentResolution;
  const officeId = f.segment.primaryLocationId;
  return useMemo<DashboardCommonFilters>(() => {
    const manualTags = f.tag ?? [];
    const tagUnion = f.regulatoryOnly
      ? Array.from(new Set([...manualTags, ...regIds]))
      : manualTags;
    const clientFilter = useResolved
      ? resolved.clientFilter
      : (f.clientFilter && f.clientFilter.length > 0
        ? f.clientFilter
        : undefined);
    return {
      startDate: f.startDate,
      endDate: f.endDate,
      assigneeFilter: f.assigneeFilter,
      clientFilter,
      officeFilter: officeId ? [officeId] : undefined,
      category: f.category,
      tag: tagUnion.length > 0 ? tagUnion : undefined,
      statuses: f.statuses,
    };
  }, [
    f.startDate, f.endDate, f.assigneeFilter, f.clientFilter, f.category,
    f.tag, f.statuses, f.regulatoryOnly, regIds,
    resolved.clientFilter, useResolved, officeId,
  ]);
}
